import type { Address } from 'viem'
import type { MulticallRetryFunction } from '../../types'
import {
  GmxDataStoreUintAbi,
  GmxGlvPricingAbi,
  GmxGlvTokenBalancesAbi,
  GmxPricingAbi,
} from './abis'
import { getGmxReadContracts } from './contracts'
import {
  GMX_GAS_KEYS,
  MAX_PNL_FACTOR_FOR_TRADERS,
  glvMaxMarketTokenBalanceAmountKey,
  glvMaxMarketTokenBalanceUsdKey,
  maxPoolAmountKey,
  maxPoolUsdForDepositKey,
  openInterestInTokensKey,
  openInterestKey,
  reserveFactorKey,
} from './keys'
import { getGmxApiHost } from './registry'
import type { GmxExecutionFees } from './types'

/** 1e30 — GMX's USD fixed-point precision for prices and pool values. */
const USD_1E30 = 1e30
/** Same precision as a bigint, for the integer reserve math. */
const USD_1E30N = 10n ** 30n
/** GMX refunds overpaid execution fees, so we over-estimate GLV per-market
 *  gas with a conservative market count rather than reading each GLV. */
const GLV_NOMINAL_MARKET_COUNT = 20n

const toUsd = (v: unknown): number =>
  typeof v === 'bigint' ? Number(v) / USD_1E30 : 0

export interface TickerPrice {
  min: bigint
  max: bigint
}
export type TickerPrices = Map<string, TickerPrice>

interface GmxTicker {
  tokenAddress: string
  minPrice: string
  maxPrice: string
}

/**
 * Fetch the GMX oracle min/max prices from `/prices/tickers`. These are in
 * exactly the fixed-point form `getMarketTokenPrice` / `getGlvTokenPrice`
 * expect, so they can be fed straight in. Keyed by lowercased token address.
 */
export const fetchGmxTickerPrices = async (
  chainId: string,
  apiUrlOverride?: string,
): Promise<TickerPrices> => {
  const host = apiUrlOverride ?? getGmxApiHost(chainId)
  const out: TickerPrices = new Map()
  if (!host) return out
  const res = await fetch(`${host}/prices/tickers`, {
    headers: { Accept: 'application/json' },
  })
  if (!res.ok) return out
  const tickers = (await res.json()) as GmxTicker[]
  for (const t of tickers) {
    try {
      out.set(t.tokenAddress.toLowerCase(), {
        min: BigInt(t.minPrice),
        max: BigInt(t.maxPrice),
      })
    } catch {
      // skip malformed price rows
    }
  }
  return out
}

const price = (prices: TickerPrices, token: string): TickerPrice | undefined =>
  prices.get(token.toLowerCase())

export interface GmxUsdValue {
  priceUsd: number
  tvlUsd: number
  /** Max withdrawable USD — what a holder of the whole vault could pull out
   *  now: pool value minus the part reserved to back open trader positions,
   *  capped at {@link tvlUsd}. The withdrawal-side liquidity. Absent when the
   *  reserve reads failed; see {@link priceGmMarkets}. */
  liquidityUsd?: number
  /** Remaining deposit capacity in USD — how much more liquidity the market
   *  can accept before hitting GMX's pool caps. Absent when the cap reads
   *  failed or no cap is configured; see {@link priceGmMarkets}. */
  depositCapacityUsd?: number
}

interface GmMarketInput {
  marketToken: string
  indexToken: string
  longToken: string
  shortToken: string
}

/**
 * Value GM market tokens in USD via `Reader.getMarketTokenPrice`, fed the
 * index/long/short oracle prices from `/prices/tickers`. Markets missing an
 * oracle price are skipped. Returns a map (lowercased GM token → value).
 * Resolves to an empty map on any failure — pricing is best-effort.
 */
export const priceGmMarkets = async (
  chainId: string,
  multicallRetry: MulticallRetryFunction,
  markets: GmMarketInput[],
  prices: TickerPrices,
): Promise<Map<string, GmxUsdValue>> => {
  const out = new Map<string, GmxUsdValue>()
  const c = getGmxReadContracts(chainId)
  if (!c) return out

  const priced = markets.filter(
    (m) =>
      price(prices, m.indexToken) &&
      price(prices, m.longToken) &&
      price(prices, m.shortToken),
  )
  if (priced.length === 0) return out

  const calls = priced.map((m) => ({
    address: c.reader,
    name: 'getMarketTokenPrice',
    params: [
      c.dataStore,
      {
        marketToken: m.marketToken,
        indexToken: m.indexToken,
        longToken: m.longToken,
        shortToken: m.shortToken,
      },
      price(prices, m.indexToken)!,
      price(prices, m.longToken)!,
      price(prices, m.shortToken)!,
      MAX_PNL_FACTOR_FOR_TRADERS,
      false,
    ],
  }))

  // DataStore reads for both liquidity figures, ten `getUint` slots per
  // market: the deposit caps (0–3) and the withdrawal-side reserve state
  // (4–9). Best-effort and independent of pricing; a failure just leaves the
  // derived fields absent.
  const SLOTS = 10
  const dsCalls = priced.flatMap((m) => {
    const market = m.marketToken as Address
    const long = m.longToken as Address
    const short = m.shortToken as Address
    const get = (key: `0x${string}`) => ({
      address: c.dataStore,
      name: 'getUint',
      params: [key],
    })
    return [
      get(maxPoolAmountKey(market, long)),
      get(maxPoolAmountKey(market, short)),
      get(maxPoolUsdForDepositKey(market, long)),
      get(maxPoolUsdForDepositKey(market, short)),
      get(openInterestInTokensKey(market, long, true)),
      get(openInterestInTokensKey(market, short, true)),
      get(openInterestKey(market, long, false)),
      get(openInterestKey(market, short, false)),
      get(reserveFactorKey(market, true)),
      get(reserveFactorKey(market, false)),
    ]
  })

  const [res, dsRes] = await Promise.all([
    multicallRetry({
      chain: chainId,
      calls,
      abi: GmxPricingAbi as any,
      maxRetries: 3,
      allowFailure: true,
    }).catch(() => null),
    multicallRetry({
      chain: chainId,
      calls: dsCalls,
      abi: GmxDataStoreUintAbi as any,
      maxRetries: 3,
      allowFailure: true,
    }).catch(() => null),
  ])
  if (!Array.isArray(res)) return out

  priced.forEach((m, i) => {
    const r = res[i]
    // 2-output fn → [marketTokenPrice, poolValueInfo]
    if (!Array.isArray(r)) return
    const info = r[1]
    const tokenPrice = r[0]
    const poolValue = info?.poolValue
    if (typeof tokenPrice !== 'bigint') return

    const value: GmxUsdValue = {
      priceUsd: toUsd(tokenPrice),
      tvlUsd: toUsd(poolValue),
    }

    const longMax = price(prices, m.longToken)?.max
    const shortMax = price(prices, m.shortToken)?.max
    const indexMax = price(prices, m.indexToken)?.max
    if (Array.isArray(dsRes) && longMax != null && shortMax != null) {
      const base = i * SLOTS
      const big = (j: number): bigint =>
        typeof dsRes[base + j] === 'bigint' ? dsRes[base + j] : 0n

      // Remaining deposit capacity = unused room under each leg's pool caps.
      const cap = computeDepositCapacityUsd(
        [info?.longTokenAmount, longMax, big(0), big(2)],
        [info?.shortTokenAmount, shortMax, big(1), big(3)],
      )
      if (cap != null) value.depositCapacityUsd = cap

      // Max withdrawable = pool value not reserved for open positions, per leg.
      if (indexMax != null) {
        // OI is stored once per (market, collateral, side); when both legs are
        // the same token the value double-counts, so halve it (GMX divisor).
        const divisor =
          m.longToken.toLowerCase() === m.shortToken.toLowerCase() ? 2n : 1n
        const reservedUsdLong = ((big(4) + big(5)) / divisor) * indexMax
        const reservedUsdShort = (big(6) + big(7)) / divisor
        const liq = computeLiquidityUsd(
          info?.longTokenUsd,
          info?.shortTokenUsd,
          reservedUsdLong,
          reservedUsdShort,
          big(8),
          big(9),
        )
        // The reserve math is on gross pool token value; the GM token's actual
        // backing is `poolValue` (net of trader PnL), so cap there — a holder
        // can never withdraw more than the pool is worth.
        if (liq != null)
          value.liquidityUsd = Math.max(0, Math.min(liq, value.tvlUsd))
      }
    }

    out.set(m.marketToken.toLowerCase(), value)
  })
  return out
}

/**
 * Max withdrawable USD for a GM market. A withdrawal reduces pool value, and
 * GMX requires `reservedUsd ≤ poolUsd × reserveFactor` to still hold after, so
 * the most that can leave one leg is `poolUsd − reservedUsd / reserveFactor`
 * (clamped at 0). Sums both legs and de-scales to USD. Returns `undefined`
 * when the per-leg pool values are missing; a reserve factor of 0 is treated
 * as unconstrained (full pool value).
 */
const computeLiquidityUsd = (
  poolUsdLong: unknown,
  poolUsdShort: unknown,
  reservedUsdLong: bigint,
  reservedUsdShort: bigint,
  reserveFactorLong: bigint,
  reserveFactorShort: bigint,
): number | undefined => {
  if (typeof poolUsdLong !== 'bigint' || typeof poolUsdShort !== 'bigint')
    return undefined
  const side = (pool: bigint, reserved: bigint, rf: bigint): bigint => {
    const floor = rf > 0n ? (reserved * USD_1E30N) / rf : 0n
    const avail = pool - floor
    return avail > 0n ? avail : 0n
  }
  const total =
    side(poolUsdLong, reservedUsdLong, reserveFactorLong) +
    side(poolUsdShort, reservedUsdShort, reserveFactorShort)
  return toUsd(total)
}

/** One leg's deposit-cap inputs: `[poolAmount, priceMax, maxPoolAmount,
 *  maxPoolUsdForDeposit]` — raw on-chain values (poolAmount/maxPoolAmount in
 *  native units, prices/USD caps in GMX 1e30 form). */
type DepositLeg = [unknown, bigint, bigint, bigint]

/**
 * Remaining deposit capacity in USD for a GM market. GMX rejects a deposit
 * once a leg's pool would breach either its token cap (`MAX_POOL_AMOUNT`) or
 * its USD cap (`MAX_POOL_USD_FOR_DEPOSIT`, valued at the token's max price),
 * so per leg the room is `min(remaining-token-room × priceMax, remaining-usd-
 * room)` — taking only the caps that are configured (`0` ⇒ not enforced).
 * Sums both legs (a deposit can use either token) and de-scales to USD.
 * Returns `undefined` only when neither leg has a usable pool value or cap.
 */
const computeDepositCapacityUsd = (
  long: DepositLeg,
  short: DepositLeg,
): number | undefined => {
  const INF = -1n // sentinel: cap not configured (unbounded)
  const side = ([poolAmount, priceMax, maxAmount, maxUsd]: DepositLeg):
    | bigint
    | undefined => {
    if (typeof poolAmount !== 'bigint') return undefined
    const poolUsd = poolAmount * priceMax
    const amountRoom =
      maxAmount > 0n
        ? (maxAmount > poolAmount ? maxAmount - poolAmount : 0n) * priceMax
        : INF
    const usdRoom =
      maxUsd > 0n ? (maxUsd > poolUsd ? maxUsd - poolUsd : 0n) : INF
    if (amountRoom === INF && usdRoom === INF) return undefined // no cap set
    if (amountRoom === INF) return usdRoom
    if (usdRoom === INF) return amountRoom
    return amountRoom < usdRoom ? amountRoom : usdRoom
  }
  const l = side(long)
  const s = side(short)
  if (l == null && s == null) return undefined
  return toUsd((l ?? 0n) + (s ?? 0n))
}

/**
 * Value GLV tokens in USD. Two stages: `getGlvInfo` (each GLV → its
 * constituent GM markets), then `getGlvTokenPrice` with the per-market
 * index-price array assembled from `/prices/tickers`. Returns the value map,
 * the largest GLV market count (used to size the GLV execution-fee gas
 * estimate), and each GLV's constituent GM markets (lowercased) so callers
 * can approximate GLV withdrawable liquidity from the per-market figures.
 * Best-effort — empty on failure.
 */
export const priceGlvVaults = async (
  chainId: string,
  multicallRetry: MulticallRetryFunction,
  glvTokens: string[],
  prices: TickerPrices,
  /** market address (lowercased) → its index token address. From `/markets`. */
  marketIndexToken: Map<string, string>,
): Promise<{
  values: Map<string, GmxUsdValue>
  maxMarketCount: number
  /** GLV address (lowercased) → its constituent GM markets (lowercased). */
  glvMarkets: Map<string, string[]>
  /** GLV address (lowercased) → its per-market balance caps + current holding,
   *  for the GLV deposit-capacity approximation. */
  glvCaps: Map<string, GlvMarketCap[]>
}> => {
  const values = new Map<string, GmxUsdValue>()
  const glvMarkets = new Map<string, string[]>()
  const glvCaps = new Map<string, GlvMarketCap[]>()
  let maxMarketCount = 0
  const c = getGmxReadContracts(chainId)
  if (!c || glvTokens.length === 0)
    return { values, maxMarketCount, glvMarkets, glvCaps }

  // Stage 1 — each GLV's constituent markets + its long/short tokens.
  let infos: any[]
  try {
    infos = await multicallRetry({
      chain: chainId,
      calls: glvTokens.map((glv) => ({
        address: c.glvReader,
        name: 'getGlvInfo',
        params: [c.dataStore, glv],
      })),
      abi: GmxGlvPricingAbi as any,
      maxRetries: 3,
      allowFailure: true,
    })
  } catch {
    return { values, maxMarketCount, glvMarkets, glvCaps }
  }

  // Stage 2 — getGlvTokenPrice per GLV. Each market is priced by its index
  // token (resolved via the `/markets` map); a GLV is skipped when any
  // index/long/short price is missing (GMX would otherwise revert).
  const calls: any[] = []
  const priceable: string[] = []
  glvTokens.forEach((glv, i) => {
    const info = infos[i]
    const long: string | undefined = info?.glv?.longToken
    const short: string | undefined = info?.glv?.shortToken
    const markets: string[] = Array.isArray(info?.markets) ? info.markets : []
    if (!long || !short || markets.length === 0) return
    maxMarketCount = Math.max(maxMarketCount, markets.length)
    glvMarkets.set(
      glv.toLowerCase(),
      markets.map((m) => m.toLowerCase()),
    )

    const lp = price(prices, long)
    const sp = price(prices, short)
    if (!lp || !sp) return

    const indexPrices: TickerPrice[] = []
    let ok = true
    for (const m of markets) {
      const idx = marketIndexToken.get(m.toLowerCase())
      const ip = idx ? price(prices, idx) : undefined
      if (!ip) {
        ok = false
        break
      }
      indexPrices.push(ip)
    }
    if (!ok) return

    calls.push({
      address: c.glvReader,
      name: 'getGlvTokenPrice',
      params: [c.dataStore, markets, indexPrices, lp, sp, glv, false],
    })
    priceable.push(glv)
  })

  if (calls.length === 0) return { values, maxMarketCount, glvMarkets, glvCaps }

  // (glv, market) pairs for the per-market GLV balance caps + current holding.
  const pairs: { glv: string; market: string }[] = []
  for (const [glv, markets] of glvMarkets)
    for (const market of markets) pairs.push({ glv, market })

  const [res, capRes, balRes] = await Promise.all([
    multicallRetry({
      chain: chainId,
      calls,
      abi: GmxGlvPricingAbi as any,
      maxRetries: 3,
      allowFailure: true,
    }).catch(() => null),
    // Two caps per pair, interleaved: [maxBalUsd, maxBalAmount].
    pairs.length
      ? multicallRetry({
          chain: chainId,
          calls: pairs.flatMap(({ glv, market }) => [
            {
              address: c.dataStore,
              name: 'getUint',
              params: [
                glvMaxMarketTokenBalanceUsdKey(
                  glv as Address,
                  market as Address,
                ),
              ],
            },
            {
              address: c.dataStore,
              name: 'getUint',
              params: [
                glvMaxMarketTokenBalanceAmountKey(
                  glv as Address,
                  market as Address,
                ),
              ],
            },
          ]),
          abi: GmxDataStoreUintAbi as any,
          maxRetries: 3,
          allowFailure: true,
        }).catch(() => null)
      : Promise.resolve(null),
    // GLV's internally-tracked GM balance for each market (`tokenBalances`).
    pairs.length
      ? multicallRetry({
          chain: chainId,
          calls: pairs.map(({ glv, market }) => ({
            address: glv as Address,
            name: 'tokenBalances',
            params: [market as Address],
          })),
          abi: GmxGlvTokenBalancesAbi as any,
          maxRetries: 3,
          allowFailure: true,
        }).catch(() => null)
      : Promise.resolve(null),
  ])
  if (!Array.isArray(res))
    return { values, maxMarketCount, glvMarkets, glvCaps }

  priceable.forEach((glv, i) => {
    const r = res[i]
    // 3-output fn → [glvTokenPrice, glvValue, glvSupply]
    if (!Array.isArray(r) || typeof r[0] !== 'bigint') return
    values.set(glv.toLowerCase(), {
      priceUsd: toUsd(r[0]),
      tvlUsd: toUsd(r[1]),
    })
  })

  // Assemble the per-(glv, market) caps for the deposit approximation.
  pairs.forEach(({ glv, market }, i) => {
    const maxBalUsd =
      Array.isArray(capRes) && typeof capRes[i * 2] === 'bigint'
        ? (capRes[i * 2] as bigint)
        : 0n
    const maxBalAmount =
      Array.isArray(capRes) && typeof capRes[i * 2 + 1] === 'bigint'
        ? (capRes[i * 2 + 1] as bigint)
        : 0n
    const balanceAmount =
      Array.isArray(balRes) && typeof balRes[i] === 'bigint'
        ? (balRes[i] as bigint)
        : 0n
    const list = glvCaps.get(glv) ?? []
    list.push({ market, maxBalUsd, maxBalAmount, balanceAmount })
    glvCaps.set(glv, list)
  })

  return { values, maxMarketCount, glvMarkets, glvCaps }
}

/** Per-(glv, market) GLV deposit-cap state. Balances/amounts in GM-token
 *  native units (18-dec); `maxBalUsd` in GMX 1e30 USD. A `0n` cap means that
 *  cap is not enforced. */
export interface GlvMarketCap {
  /** GM market token address, lowercased. */
  market: string
  maxBalUsd: bigint
  maxBalAmount: bigint
  /** GLV's current GM-token holding for this market. */
  balanceAmount: bigint
}

/**
 * Estimate per-chain minimum execution fees for the four GM/GLV request
 * types. Reads the DataStore gas-limit + multiplier config in one multicall
 * and multiplies by `gasPriceWei`. Returns `undefined` if the chain is
 * unsupported or the reads/gas-price fail.
 */
export const fetchGmxExecutionFees = async (
  chainId: string,
  multicallRetry: MulticallRetryFunction,
  gasPriceWei: bigint,
): Promise<GmxExecutionFees | undefined> => {
  const c = getGmxReadContracts(chainId)
  if (!c) return undefined

  const keys = [
    GMX_GAS_KEYS.estimatedGasFeeBaseAmount,
    GMX_GAS_KEYS.estimatedGasFeeMultiplierFactor,
    GMX_GAS_KEYS.depositGasLimit,
    GMX_GAS_KEYS.withdrawalGasLimit,
    GMX_GAS_KEYS.glvDepositGasLimit,
    GMX_GAS_KEYS.glvWithdrawalGasLimit,
    GMX_GAS_KEYS.glvPerMarketGasLimit,
  ]
  let res: any[]
  try {
    res = await multicallRetry({
      chain: chainId,
      calls: keys.map((key) => ({
        address: c.dataStore,
        name: 'getUint',
        params: [key],
      })),
      abi: GmxDataStoreUintAbi as any,
      maxRetries: 3,
      allowFailure: true,
    })
  } catch {
    return undefined
  }
  const big = (i: number): bigint => (typeof res[i] === 'bigint' ? res[i] : 0n)
  const base = big(0)
  const mult = big(1)
  if (base === 0n && mult === 0n) return undefined

  const PRECISION = 10n ** 30n
  // GMX SDK: adjusted = base + estimatedGasLimit * multiplier / 1e30
  const adjusted = (opGas: bigint): bigint => base + (opGas * mult) / PRECISION
  const fee = (opGas: bigint): string =>
    (adjusted(opGas) * gasPriceWei).toString()

  const glvExtra = big(6) * GLV_NOMINAL_MARKET_COUNT
  return {
    chainId,
    gasPriceWei: gasPriceWei.toString(),
    deposit: fee(big(2)),
    withdrawal: fee(big(3)),
    glvDeposit: fee(big(4) + glvExtra),
    glvWithdrawal: fee(big(5) + glvExtra),
  }
}
